import { DialogActions, DialogContent } from '@mui/material';
import { Alert, AlertTitle } from '@mui/material';
import { Modal } from '../../../../../ui/components/pure/modal/modal.component';
import { TextButton } from '../../../../../ui/components/pure/text-button/text-button.component';
import { parseErrorMessage } from '../../../../../core/api/api.context';
import {
  ArchiveDeliveryActionHandlers,
  ArchiveDeliveryActionProps,
  ArchiveDeliveryActionState,
  useArchiveDeliveryAction
} from './archive-delivery-action.hook';

export function ArchiveDeliveryModal({ delivery, ...rest }: ArchiveDeliveryActionProps) {
  const [state, handlers, canArchive, isMigrated] = useArchiveDeliveryAction({ delivery, ...rest });

  return (
    <ArchiveDeliveryModalComponent state={state} handlers={handlers} canArchive={canArchive} isMigrated={isMigrated} />
  );
}

/* This component is exported only for use in Storybook */
export function ArchiveDeliveryModalComponent({
  handlers,
  state,
  canArchive,
  isMigrated
}: {
  handlers: ArchiveDeliveryActionHandlers;
  state: ArchiveDeliveryActionState;
  canArchive: boolean;
  isMigrated: boolean;
}) {
  return (
    <Modal
      title="Archive Delivery?"
      open={true}
      loading={state.loading}
      maxWidth="xs"
      onClose={handlers.onCancelIntent}
    >
      <DialogContent style={{ padding: '16px 24px', fontSize: '14px' }}>
        {state.error && (
          <p>
            <Alert variant="outlined" severity="error">
              <AlertTitle>Something went wrong</AlertTitle>
              {parseErrorMessage(state.error.message)}
            </Alert>
          </p>
        )}
        {state.current === 'ARCHIVE_REQUEST_SUCCESS' ? (
          <p>
            <Alert variant="outlined" severity="success">
              <AlertTitle>The Delivery has been successfully archived</AlertTitle>
              Once you close this modal, you will return to the Deliveries list page.
            </Alert>
          </p>
        ) : (
          <ArchiveDeliveryModalText canArchive={canArchive} isMigrated={isMigrated} />
        )}
      </DialogContent>
      <DialogActions style={{ borderTop: '1px solid #EBEDF2', padding: '16px 24px' }}>
        <ArchiveDeliveryModalActions
          state={state}
          handlers={handlers}
          canArchive={canArchive}
        ></ArchiveDeliveryModalActions>
      </DialogActions>
    </Modal>
  );
}

export function ArchiveDeliveryModalActions({
  state,
  handlers,
  canArchive
}: {
  state: ArchiveDeliveryActionState;
  handlers: ArchiveDeliveryActionHandlers;
  canArchive: boolean;
}) {
  const callToActionButtonHandler =
    state.current === 'ARCHIVE_REQUEST_SUCCESS' ? handlers.onSuccessArchiveDelivery : handlers.onConfirmArchiveDelivery;

  if (state.current === 'ARCHIVE_REQUEST_AC_MIGRATION') {
    return (
      <>
        <TextButton color="secondary" variant="outlined" onClick={() => handlers.onCancelIntent()}>
          Close
        </TextButton>
        <TextButton
          key="doneButton"
          color="primary"
          variant="outlined"
          onClick={() => handlers.onOpenLinkToAcDelivery()}
        >
          View in old portal
        </TextButton>
      </>
    );
  }

  return (
    <>
      {state.current !== 'ARCHIVE_REQUEST_SUCCESS' && state.current !== 'ARCHIVE_REQUEST_AC_MIGRATION' && (
        <TextButton color="secondary" variant="outlined" onClick={() => handlers.onCancelIntent()}>
          {canArchive ? 'Cancel' : 'Close'}
        </TextButton>
      )}
      {state.current === 'ARCHIVE_REQUEST_SUCCESS' ? (
        <TextButton key="doneButton" color="secondary" variant="outlined" onClick={() => callToActionButtonHandler()}>
          Done
        </TextButton>
      ) : canArchive ? (
        <TextButton key="archiveButton" color="danger" variant="outlined" onClick={() => callToActionButtonHandler()}>
          Archive
        </TextButton>
      ) : null}
    </>
  );
}

export function ArchiveDeliveryModalText({ canArchive, isMigrated }: { canArchive: boolean; isMigrated: boolean }) {
  return (
    <>
      <p>
        {isMigrated ? (
          <Alert variant="outlined" severity="error">
            <AlertTitle>Unable to Archive</AlertTitle>
            This delivery is managed in AC, as it's running an AC Kiosk. Please archive it in the old portal.
          </Alert>
        ) : (
          <>
            Archiving will hide the Delivery from the Deliveries list and prevent it from being counted in the
            statistics.
          </>
        )}
      </p>
      {canArchive ? (
        <p> Are you sure?</p>
      ) : (
        <p>You will need to collect, cancel or retrieve the Delivery before you can do this.</p>
      )}
    </>
  );
}
